import React, {useEffect, useReducer} from 'react';
import {useIntl} from 'react-intl';
import { Lx } from '../../../../../../i18n/consts';
import Selector from '../../../../../../components/shared/nextgen/selector';
import {FilterActionType, FilterChangeAction, FilterState} from '../filter/types';
import {REmployees} from '../../../../../../features/roles/types';
import VacationFilterListPreview from './filter-list-preview';
import { initialVacationStates } from '../../../../../../features/vacation/reportSlice';
import {useAppSelector} from '../../../../../../hooks/hooks';
import PropertyFilterPopup from '../../../../../../components/shared/property-filter-popup';

interface Props {
    employees: REmployees[];
    onChange: (filter: FilterState) => void;
    hasSelected: boolean;
    disabled?: boolean;
    compactNarrowDesktop?: boolean;
    disableEmployeesFilter?: boolean;
}

interface State {
    draft: FilterState;
    current: FilterState;
}

export default function VacationFilterListPopup(props: Props) {
    const intl = useIntl();
    const [selectedFiltersCount, setSelectedFiltersCount] = React.useState(0);
    const filter = useAppSelector(s => s.timeTrackingFilters.filter.propertyFilter);
    const statusesFilter = useAppSelector(s => s.vacationsReport.filter.propertyFilter.selectedVacationStates);


    const emptyFilterState: FilterState = {
        selectedEmployees: [],
        selectedVacationStates: []
    }

    const initialFilterState: FilterState = {
        ...emptyFilterState,
    }

    const initialState: State = {
        current: initialFilterState,
        draft: initialFilterState,
    }

    const handlePropertyChange = (action: FilterActionType, state: FilterState, payload: any): FilterState => {
        switch (action) {
            case 'EMPLOYEES_UPDATE':
                state.selectedEmployees = payload ?? [];
                break;
            case 'STATES_UPDATE':
                state.selectedVacationStates = payload ?? [];
                break;
        }

        return state;
    }

    const reducer = (state: State, action: Partial<FilterChangeAction>): State => {
        const nextState = {...state};
        switch (action.action) {
            case 'HYDRATE': {
                const incoming = (action.payload as FilterState) ?? emptyFilterState;
                nextState.current = {...incoming};
                nextState.draft = {...incoming};
                break;
            }
            case 'APPLY':
                nextState.current = {...state.draft};
                break;
            case 'CANCEL':
                nextState.draft = {...state.current};
                break;
            case 'RESET':
                nextState.draft = {...emptyFilterState};
                break;
            default:
                nextState.draft = handlePropertyChange(action.action as FilterActionType, nextState.draft, action.payload);
                break;
        }

        const currentFilters = Object.entries(nextState.current)
            .filter(([key]) => !(props.disableEmployeesFilter && key === 'selectedEmployees'))
            .map(([, value]) => value);

        setSelectedFiltersCount(Object.values(currentFilters).filter((x) => x.length > 0).length);

        return nextState;
    }

    const [filterState, dispatch] = useReducer(reducer, initialState);

    const resetFilter = () => {
        dispatch({ action: 'RESET' })
    }

    const applyFilter = () => {
        dispatch({ action: 'APPLY' });
        props.onChange(filterState.draft);
    }

    const handleClose = () => {
        dispatch({ action: 'CANCEL' });
    };
    useEffect(() => {
        if(props.hasSelected)
            return;
        dispatch({action:'HYDRATE', payload: initialFilterState});
    }, [props.hasSelected]);

    useEffect(() => {
        dispatch({
            action: 'HYDRATE',
            payload: {
                selectedEmployees: props.disableEmployeesFilter ? [] : filter.selectedEmployees,
                selectedVacationStates: statusesFilter
            }
        });
    }, [filter, props.disableEmployeesFilter, statusesFilter]);

    return (
        <PropertyFilterPopup
            selectedFiltersCount={selectedFiltersCount}
            previewContent={
                <VacationFilterListPreview
                    selectedEmployees={props.disableEmployeesFilter ? [] : filterState.current.selectedEmployees}
                    selectedVacationStates={filterState.current.selectedVacationStates}
                />
            }
            onReset={resetFilter}
            onApply={applyFilter}
            onCancel={handleClose}
            disabled={props.disabled}
            compactNarrowDesktop={props.compactNarrowDesktop}
        >
            {!props.disableEmployeesFilter && (
                <Selector id={'employeeFilter'}
                    label={Lx.General.EMPLOYEES}
                    items={props.employees.map((employee) => ({ id: employee.id, name: employee.name, originalId: employee.id }))}
                    name={Lx.General.EMPLOYEES}
                    selected={filterState.draft.selectedEmployees}
                    maxWidth={368}
                    setSelectedValues={(values) => dispatch({ action: 'EMPLOYEES_UPDATE', payload: values })} intl={intl}
                    disabled={props.disabled}/>
            )}
            <Selector id={'vacationStateFilter'}
                label={Lx.Vacation.STATE}
                items={initialVacationStates.map((x) => ({...x, name: intl.formatMessage({ id: x.name })}))}
                name={Lx.Vacation.STATE}
                selected={filterState.draft.selectedVacationStates}
                maxWidth={368}
                setSelectedValues={(values) => dispatch({ action: 'STATES_UPDATE', payload: values })}
                intl={intl}
                disabled={props.disabled}/>
        </PropertyFilterPopup>
    )
}
